import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ArrowLeft, ZoomIn, ZoomOut, Printer, Download, Loader2 } from "lucide-react";
import { getSignedPdfArrayBuffer, getFileArrayBuffer } from "../../api";
import DownloadFilesModal from "./DownloadFilesModal";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const BASE_WIDTH = 720;
const MIN_SCALE = 0.5;
const MAX_SCALE = 2.4;

const PdfViewer = ({ onAction }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const meta = location.state || {};
  const docIndex = meta.docIndex;
  // For not-yet-completed envelopes show the original (unsigned) document; the
  // signed PDF only exists once the whole envelope is completed.
  const showOriginal = meta.completed === false && !!meta.storagePath;

  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [current, setCurrent] = useState(1);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const pageRefs = useRef([]);

  useEffect(() => {
    let url = null;
    let alive = true;
    setLoading(true);
    const fetcher = showOriginal
      ? getFileArrayBuffer(meta.storagePath)
      : getSignedPdfArrayBuffer(id, docIndex);
    fetcher
      .then((buf) => {
        if (!alive) return;
        url = window.URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
        setBlobUrl(url);
        setLoading(false);
      })
      .catch(() => {
        onAction("Could not open the signed document");
        navigate(-1);
      });
    return () => {
      alive = false;
      if (url) window.URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const file = useMemo(() => (blobUrl ? blobUrl : null), [blobUrl]);

  const scrollToPage = (n) => {
    const el = pageRefs.current[n - 1];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handlePrint = () => {
    if (!blobUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    iframe.src = blobUrl;
    iframe.onload = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        window.open(blobUrl, "_blank");
      }
    };
    document.body.appendChild(iframe);
  };

  const zoom = (dir) =>
    setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, +(s + dir * 0.2).toFixed(2))));

  return (
    <div className="min-h-screen" data-testid="pdf-viewer-page">
      {/* Toolbar */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#E7E0D8] bg-[#FBF7F2]/90 px-4 py-3 backdrop-blur-md dark:border-white/10 dark:bg-[#141019]/85 md:px-6">
        <button
          data-testid="viewer-back-btn"
          onClick={() => navigate(-1)}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F0EBE5] text-[#1A1815] transition-colors hover:bg-[#E7E0D8] dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} />
        </button>
        <h1 className="min-w-0 flex-1 truncate text-base font-bold tracking-tight text-[#1A1815] dark:text-white">
          {meta.name || "Signed document"}
        </h1>
        <div className="flex items-center gap-1 rounded-full bg-[#F0EBE5] p-1 dark:bg-white/5">
          <button
            data-testid="zoom-out-btn"
            onClick={() => zoom(-1)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#1A1815] transition-colors hover:bg-white dark:text-white dark:hover:bg-white/10"
          >
            <ZoomOut className="h-4 w-4" strokeWidth={1.9} />
          </button>
          <span className="w-12 text-center text-xs font-semibold text-[#8B8680] dark:text-white/55">
            {Math.round(scale * 100)}%
          </span>
          <button
            data-testid="zoom-in-btn"
            onClick={() => zoom(1)}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[#1A1815] transition-colors hover:bg-white dark:text-white dark:hover:bg-white/10"
          >
            <ZoomIn className="h-4 w-4" strokeWidth={1.9} />
          </button>
        </div>
        <button
          data-testid="print-btn"
          onClick={handlePrint}
          className="flex h-10 items-center gap-2 rounded-xl border border-[#E7E0D8] px-4 text-sm font-semibold text-[#1A1815] transition-colors hover:bg-[#F0EBE5] dark:border-white/10 dark:text-white dark:hover:bg-white/5"
        >
          <Printer className="h-4 w-4" strokeWidth={1.9} />
          <span className="hidden sm:inline">Print</span>
        </button>
        {!showOriginal && (
        <button
          data-testid="viewer-download-btn"
          onClick={() => setDownloadOpen(true)}
          className="flex h-10 items-center gap-2 rounded-xl bg-[#1A1815] px-4 text-sm font-semibold text-white transition-transform hover:scale-[1.03] active:scale-95 dark:bg-white dark:text-black"
        >
          <Download className="h-4 w-4" strokeWidth={1.9} />
          <span className="hidden sm:inline">Download</span>
        </button>
        )}
      </header>

      {loading || !file ? (
        <div className="flex min-h-[70vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
        </div>
      ) : (
        <Document
          file={file}
          onLoadSuccess={({ numPages: n }) => setNumPages(n)}
          onLoadError={() => onAction("Could not render document")}
          loading={
            <div className="flex min-h-[70vh] items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-[#8B8680] dark:text-white/50" />
            </div>
          }
        >
          <div className="flex">
            {/* Thumbnails rail */}
            <aside className="thin-scroll sticky top-[65px] hidden max-h-[calc(100vh-65px)] w-40 shrink-0 overflow-y-auto border-r border-[#E7E0D8] px-3 py-4 dark:border-white/10 lg:block">
              {Array.from({ length: numPages }, (_, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i + 1}`}
                  onClick={() => scrollToPage(i + 1)}
                  className={`mb-3 block w-full overflow-hidden rounded-md border-2 bg-white transition-colors ${
                    current === i + 1 ? "border-[#7A6FF0]" : "border-[#E7E0D8] hover:border-[#C9C2BA] dark:border-white/10"
                  }`}
                >
                  <Page pageNumber={i + 1} width={128} renderAnnotationLayer={false} renderTextLayer={false} />
                  <span className="block bg-[#F6EFE8] py-1 text-center text-[11px] font-semibold text-[#8B8680] dark:bg-white/5 dark:text-white/45">
                    {i + 1}
                  </span>
                </button>
              ))}
            </aside>

            {/* Main scrollable pages */}
            <main className="thin-scroll max-h-[calc(100vh-65px)] flex-1 overflow-y-auto bg-[#F0EBE5] px-4 py-6 dark:bg-black/30">
              <div className="mx-auto flex flex-col items-center gap-6">
                {Array.from({ length: numPages }, (_, i) => (
                  <div
                    key={i}
                    ref={(el) => (pageRefs.current[i] = el)}
                    className="overflow-hidden rounded-lg bg-white shadow-xl"
                  >
                    <Page
                      pageNumber={i + 1}
                      width={BASE_WIDTH * scale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                    />
                  </div>
                ))}
              </div>
            </main>
          </div>
        </Document>
      )}

      <DownloadFilesModal
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        docId={id}
        baseName={meta.name || "document"}
        docCount={meta.docCount || 1}
        onAction={onAction}
      />
    </div>
  );
};

export default PdfViewer;
